import React, { useRef, useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { generateUniqueId } from '../FiltersSidebar/utils/uniqueId'
import FileNameField from './FileNameField'

const getInputId = id => id || generateUniqueId('file-input')

// Reset so re-picking the same file fires onChange again (parent owns the list).
// Guarded: headless DOMs (test env) don't implement the file value setter.
const resetInputValue = input => {
  try {
    input.value = ''
  } catch {
    // Intentionally empty
  }
}

const getHelpTextFromAccept = (accept, helpText) => {
  if (helpText)
    return helpText

  let formatText = ''
  if (accept === '.pdf')
    formatText = 'PDF'
  else if (accept.includes('image/'))
    formatText = 'PNG, JPEG'
  else if (accept === '*' || !accept)
    return null

  return formatText ? `Format autorisé : ${formatText} (poids max : 10 MB)` : null
}

const FileInput = ({
  accept = '*',
  placeholder = 'Aucun fichier sélectionné',
  buttonText = 'Choisir un fichier',
  buttonClass = 'btn-primary',
  onChange,
  value = '',
  onDelete,
  disabled = false,
  name = '',
  id = '',
  helpText = null,
  errors = null,
  allowDelete = true,
  multiple = false
}) => {
  const [fileName, setFileName] = useState(value || '')
  const fileInputRef = useRef(null)
  const inputIdRef = useRef(getInputId(id))
  const inputId = inputIdRef.current

  const handleButtonClick = useCallback(() => {
    if (fileInputRef.current && !disabled)
      fileInputRef.current.click()
  }, [disabled])

  const handleFileChange = useCallback(event => {
    const files = Array.from(event.target.files)
    if (files.length === 0)
      return
    if (multiple) {
      resetInputValue(event.target)
      onChange?.(files)
      return
    }
    const [file] = files
    setFileName(file.name)
    onChange?.(file)
  }, [multiple, onChange])

  const handleDelete = useCallback(event => {
    event.preventDefault()
    event.stopPropagation()
    setFileName('')
    if (fileInputRef.current)
      fileInputRef.current.value = ''

    if (onDelete)
      onDelete()
  }, [onDelete])

  useEffect(() => {
    setFileName(value || '')
  }, [value])

  const displayHelpText = getHelpTextFromAccept(accept, helpText)

  return (
    <div className="file-input-wrapper">
      <div className="input-group">
        <div className="input-group-prepend">
          <button
            type="button"
            className={`btn ${buttonClass}`}
            disabled={disabled}
            onClick={handleButtonClick}
          >
            {buttonText}
          </button>
        </div>
        {multiple ? null : (
          <FileNameField
            allowDelete={allowDelete}
            disabled={disabled}
            errors={errors}
            fileName={fileName}
            inputId={inputId}
            name={name}
            placeholder={placeholder}
            onDeleteClick={handleDelete}
          />
        )}
      </div>
      {errors ? (
        <div>
          <small className="text-danger">{errors[0]}</small>
        </div>
      ) : null}
      {displayHelpText ? (
        <small className="text-muted">
          {displayHelpText}
        </small>
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        id={inputId}
        name={name}
        className="d-none"
        accept={accept}
        disabled={disabled}
        multiple={multiple}
        aria-label={buttonText}
        onChange={handleFileChange}
      />
    </div>
  )
}

FileInput.propTypes = {
  multiple: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func
}

FileInput.defaultProps = {
  multiple: false,
  onDelete: null
}

export default FileInput
